import { DndContext, closestCenter } from '@dnd-kit/core'
import { SortableContext } from '@dnd-kit/sortable'
import { restrictToParentElement } from '@dnd-kit/modifiers'

import { Box, Button, Stack } from '@mui/material'
import { PlusIcon } from 'shared/icons'
import Grid from '@mui/material/Grid'

import { Loading } from 'components/controls/loading'
import { BlockCaption } from 'components/controls/blockCaption'

import { useRemoveSearchsSenmoni, useListSearchsSenmoni, useSortSearchsSenmoni } from 'interface/search/senmoni'

import { useSortableList } from '../container/useSortableList'
import { useSortableListEdit } from '../container/useSortableListEdit'
import { EdittableDataListRecord, StaticDataListRecord } from '../components/record'

import { EditSearchsSenmoniDialog } from './dialog/edit'
import { AddSearchsSenmoniDialog } from './dialog/add'
import { ErrorInfo } from 'components/controls/errorInfo'

const SearchsSenmoniCaption = "対象専門医"

const gridRules = {
  size: {
    xs: 12,
    sm: 12,
    md: 6,
    lg: 4,
    xl: 3
  }
}

type EdittableSearchsSenmoniListBlockProps = {
  searchId: number
}
const EdittableSearchsSenmoniListBlock = ({
  searchId,
}: EdittableSearchsSenmoniListBlockProps) => {
  const { loading, data, error } = useListSearchsSenmoni(searchId)
  const list = useSortableList({ list: data?.list ?? [] })
  const { sortableList } = list
  const [postState, post] = useSortSearchsSenmoni(searchId)
  const [removeState, remove] = useRemoveSearchsSenmoni(searchId)
  const {
		modal, closeModal,
		onAdd, onEditIt, onRemoveIt,
		onSubmitEdit, onSubmitAdd,
		onDragEnd
  } = useSortableListEdit({
    ...list,
    post: (data) => post(data?.map(r => r.searchsSenmoniId) ?? [])
  })
  return (
    <Stack direction="column" position="relative">
      <Stack direction="row" spacing={1} alignItems="flex-end" width="100%" >
        <Box flexGrow={1}><BlockCaption flexGrow={1} caption={SearchsSenmoniCaption}/></Box>
        <Button
          startIcon={<PlusIcon />}
          variant="contained"
          size="small"
          sx={{ width: 'auto' }}
          onClick={onAdd}
        >
          追加
        </Button>
      </Stack>
      {error && <ErrorInfo error={error}/>}
      {(loading || postState.posting || removeState.posting) && <Loading/>}
      {(postState.posting || removeState.posting) && <Loading />}

      {sortableList.length > 0 && (
        <Grid container flexGrow={1} spacing={1} marginTop={1}>
          <DndContext
            collisionDetection={closestCenter}
            modifiers={[restrictToParentElement]}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={sortableList}>
              {sortableList.map((senmoni, i) => (
                <EdittableDataListRecord
                  key={`senmoni-${i}`}
                  data={senmoni}
                  displayName={senmoni.senmoniName ?? 'No name'}
                  onEditIt={() => {
                    onEditIt(senmoni)
                  }}
                  onRemoveIt={() => {
                    remove(senmoni.searchsSenmoniId)
                    onRemoveIt(senmoni)
                  }}
                  {...gridRules}
                />
              ))}
            </SortableContext>
          </DndContext>
        </Grid>
      )}

      { modal?.command === 'add' ? (
        <AddSearchsSenmoniDialog
          searchId={searchId}
          onSubmit={onSubmitAdd}
          onClose={closeModal}
        />
      ) : (modal?.command === 'edit' && modal.data) ? (
        <EditSearchsSenmoniDialog
          searchId={searchId}
          senmoni={modal.data}
          onSubmit={onSubmitEdit}
          onClose={closeModal}
        />
      ) : (
        <></>
      )}
    </Stack>
  )
}

type StaticSearchsSenmoniListBlockProps = {
  searchId: number
}
const StaticSearchsSenmoniListBlock = ({
  searchId
}: StaticSearchsSenmoniListBlockProps) => {
  const { loading, data, error } = useListSearchsSenmoni(searchId)
  if (loading || !data || data.list.length === 0) return <></>
  return (
    <Stack direction="column" position="relative">
      <BlockCaption caption={SearchsSenmoniCaption} />
      {error && <ErrorInfo error={error}/>}

      <Grid container flexGrow={1} spacing={1} marginTop={0.5}>
        {data.list.map((senmoni, i) => (
          <StaticDataListRecord
            key={`senmoni-${i}`}
            displayName={senmoni.senmoniName ?? 'No name'}
            {...gridRules}
          />
        ))}
      </Grid>
    </Stack>
  )
}

type SearchsSenmoniListBlockProps = {
  edittable: boolean
  searchId: number
}
export const SearchsSenmoniListBlock = ({
  edittable,
  searchId
}: SearchsSenmoniListBlockProps) => edittable ? (
  <EdittableSearchsSenmoniListBlock
    searchId={searchId}
  />
) : (
  <StaticSearchsSenmoniListBlock
    searchId={searchId}
  />
)
